import { useEffect, useState, type FormEvent } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import Dialog from "@mui/material/Dialog";
import styles from "./ProfileSettings.module.scss";
import { requestHeaders } from "./profileApi";

export interface TeamMember {
  email: string;
  userGroup: "admin" | "user";
  role: string;
}

const memberRoles = ["Dentist", "Hygienist", "Receptionist", "Dental Nurse", "Dental Therapist", "Practice Manager"];

export default function AddTeamMemberModal({
  members,
  onAdd,
  onClose,
}: {
  members: TeamMember[];
  onAdd: (member: TeamMember) => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState("");
  const [error, setError] = useState("");
  const subscriptionQuery = useQuery({
    queryKey: ["myPlan"],
    queryFn: async () => {
      const { data } = await axios.get<{ subscription_name?: string }>("/subscription", { headers: requestHeaders() });
      return data;
    },
    retry: false,
  });
  const subscriptionName = subscriptionQuery.data?.subscription_name?.trim() || "";
  const isAssociateSubscription = /^associate(?:\s|$)/i.test(subscriptionName);
  const rolesReady = subscriptionQuery.isSuccess && !!subscriptionName && !subscriptionQuery.isFetching;
  const availableRoles = rolesReady
    ? memberRoles.filter((item) => !isAssociateSubscription || (item !== "Dentist" && item !== "Hygienist"))
    : [];

  useEffect(() => {
    if (isAssociateSubscription && (role === "Dentist" || role === "Hygienist")) setRole("");
  }, [isAssociateSubscription, role]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const member: TeamMember = { email: email.trim(), userGroup: isAdmin ? "admin" : "user", role };
    if (!member.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!rolesReady || !availableRoles.includes(role)) {
      setError("Please select a role available for your subscription.");
      return;
    }
    if (members.some((item) => item.email.toLowerCase() === member.email.toLowerCase())) {
      setError("A team member with this email has already been added.");
      return;
    }
    onAdd(member);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby="add-member-title"
      aria-describedby="add-member-description"
      maxWidth={false}
      className={styles.memberDialogRoot}
      PaperProps={{ className: styles.memberDialog }}
    >
      <button type="button" className={styles.modalClose} aria-label="Close add team member" onClick={onClose}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
      </button>
      <div className={styles.memberModalLayout}>
        <aside className={styles.memberIllustration}>
          
          <svg className={styles.courierScene} viewBox="0 0 280 260" fill="none" aria-hidden="true">
            <circle cx="139" cy="128" r="100" fill="#FFE8D2" />
            <circle cx="222" cy="54" r="7" fill="#F9BD80" />
            <circle cx="43" cy="183" r="5" fill="#F9BD80" />
            <path d="m43 68 5-10 5 10-5 10-5-10Z" fill="#EE7D22" />
            <path d="M195 81c47-17 68 21 31 44" stroke="#E4A568" strokeWidth="2" strokeDasharray="5 6" strokeLinecap="round" />
            <ellipse cx="145" cy="235" rx="79" ry="9" fill="#EBCFB7" opacity=".55" />
            <g className={styles.courierBody}>
              <path d="m119 166-7 59h19l13-54 9 54h19l-5-62" fill="#253F64" />
              <path d="M110 225h22v9h-34c0-6 5-9 12-9ZM151 225h20l12 9h-32v-9Z" fill="#142C4C" />
              <path d="M116 100c-18 3-29 21-34 45l17 7 13-22-3 42h66l-4-46 20 10 9-15-33-20" fill="#EE7D22" />
              <path d="m117 99 24 19 22-19" fill="#FFF9F3" />
              <path d="M131 88v16l11 10 11-11V87" fill="#E8AB82" />
              <rect x="119" y="49" width="47" height="48" rx="22" fill="#F5C09A" />
              <path d="M120 66c-7-15 5-31 22-31 17 0 27 11 24 29l-9-7-37 9Z" fill="#253F64" />
              <path d="M116 52c0-17 13-26 28-26 16 0 26 11 26 26" fill="#EE7D22" />
              <path d="M113 52h61c0 8-15 12-30 10l-31-10Z" fill="#C75F12" />
              <rect x="138" y="34" width="14" height="10" rx="3" fill="#FFF4E8" />
              <circle cx="136" cy="72" r="2" fill="#253F64" />
              <circle cx="155" cy="72" r="2" fill="#253F64" />
              <path d="M139 84c4 4 9 4 13-1" stroke="#A76142" strokeWidth="2.5" strokeLinecap="round" />
              <path d="m119 104 39 66" stroke="#884A24" strokeWidth="7" />
              <rect x="143" y="142" width="40" height="36" rx="6" fill="#A96232" />
              <path d="M143 149h40l-9 13h-21l-10-13Z" fill="#BE7845" />
              <path d="m84 147 13 4 11 16" stroke="#F5C09A" strokeWidth="13" strokeLinecap="round" />
              <g className={styles.courierLetter}>
                <rect x="181" y="91" width="55" height="39" rx="5" fill="white" stroke="#E4A568" strokeWidth="1.5" />
                <path d="m184 96 24 18 25-18M184 126l15-14M232 126l-15-14" stroke="#EE7D22" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="231" cy="91" r="11" fill="#253F64" />
                <path d="m226 91 3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <path d="m190 128 11-4" stroke="#F5C09A" strokeWidth="12" strokeLinecap="round" />
            </g>
          </svg>
          <h3>A great team starts<br />with one hello.</h3>
          <p>Bring your people together for better patient care.</p>
          
        </aside>
        <div className={styles.memberModalContent}>
          <div className={styles.modalHeading}>
           
            <h2 id="add-member-title">Add team member</h2>
            <p id="add-member-description">Make room for someone new. Add their details to get started.</p>
          </div>
          <form aria-label="Add team member" className={styles.memberForm} onSubmit={handleSubmit}>
            <label className={styles.field}>
              <span>Email address <span className={styles.requiredMark}>*</span></span>
              <input autoFocus required type="email" name="email" autoComplete="email" maxLength={254} placeholder="alex@yourpractice.com" value={email} onChange={(event) => { setEmail(event.target.value); setError(""); }} aria-invalid={error.includes("email") || undefined} aria-describedby={error ? "member-form-error" : undefined} />
            </label>
            <fieldset className={styles.userGroupField}>
              <legend>User group</legend>
              <label className={styles.adminCheckbox}>
                <input type="checkbox" name="isAdmin" checked={isAdmin} onChange={(event) => { setIsAdmin(event.target.checked); setError(""); }} />
                <span>Make this user an admin</span>
              </label>
            </fieldset>
            <label className={styles.field}>
              <span>Role <span className={styles.requiredMark}>*</span></span>
              <select required name="role" disabled={!rolesReady} value={availableRoles.includes(role) ? role : ""} onChange={(event) => { setRole(event.target.value); setError(""); }}>
                <option value="" disabled>{subscriptionQuery.isFetching ? "Loading roles..." : "Select a role"}</option>
                {availableRoles.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            {!subscriptionQuery.isFetching && !rolesReady && (
              <div className={`${styles.notice} ${styles.error}`} role="alert">
                Could not load your subscription details.
                <button className={styles.outlineButton} type="button" onClick={() => { setError(""); void subscriptionQuery.refetch(); }}>Try again</button>
              </div>
            )}
            {error && <p id="member-form-error" className={`${styles.notice} ${styles.error}`} role="alert">{error}</p>}
            <div className={styles.memberModalActions}>
              <button type="button" className={styles.outlineButton} onClick={onClose}>Cancel</button>
              <button type="submit" disabled={!rolesReady} className={styles.primaryButton}>Add member <span aria-hidden="true">↗</span></button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}
